import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ObjectiveCategory } from '../types';
import { performanceService } from '../performanceService';
import { administrationService, Department } from '../../../administration/administrationService';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface CreateObjectiveModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateObjectiveModal: React.FC<CreateObjectiveModalProps> = ({
  onClose,
  onSuccess,
}) => {
  useEscapeKey(onClose);
  const { showError } = useDialog();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ObjectiveCategory>('OPERATIONAL');
  const [department, setDepartment] = useState('');
  const [targetDate, setTargetDate] = useState('2026-12-31');
  const [loading, setLoading] = useState(false);
  const [deptList, setDeptList] = useState<Department[]>([]);

  useEffect(() => {
    administrationService.getDepartments().then((depts) => {
      const activeDepts = depts.filter((d) => d.status === 'ACTIVE');
      setDeptList(activeDepts);
      if (activeDepts.length > 0) {
        setDepartment(activeDepts[0].name);
      }
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await performanceService.createObjective({
        title,
        description,
        category,
        department,
        targetDate,
        createdBy: 'SAFETY_MANAGER',
      });
      onSuccess();
    } catch (err: any) {
      showError(err, 'Failed to Define Safety Objective');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: '620px' }}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h3 style={{ fontSize: '19px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
              Define Safety Performance Objective
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>
              ICAO SMS Pillar 3 - Safety Assurance policy target & measurable threshold.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field">
              <label>Objective Title *</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Zero High-Risk Runway Incursions & Controlled Rest Exceedances"
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Objective Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ObjectiveCategory)}
                >
                  <option value="OPERATIONAL">Operational Safety</option>
                  <option value="MAINTENANCE">Technical / Maintenance</option>
                  <option value="GROUND">Ground Operations & Ramp</option>
                  <option value="SECURITY">Aviation Security</option>
                  <option value="CULTURE">Safety Culture & Reporting</option>
                </select>
              </div>

              <div className="form-field">
                <label>Responsible Department *</label>
                <select
                  required
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                >
                  {deptList.length === 0 ? (
                    <option value="">Select Department</option>
                  ) : (
                    deptList.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name} ({d.department_code})
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div className="form-field">
              <label>Target Achievement Date *</label>
              <input
                type="date"
                required
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Scope & Qualitative Narrative *</label>
              <textarea
                required
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Operational scope, SMS justification, baseline metric, and expected safety improvement..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Creating...' : 'Create Objective'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateObjectiveModal;
